import React from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import {
  ShieldCheckIcon,
  HandshakeIcon,
  PackageIcon,
  ChatCircleDotsIcon,
  CameraIcon,
  StorefrontIcon,
  ScalesIcon,
  XCircleIcon,
  CheckCircleIcon,
  WarningCircleIcon,
  SealCheckIcon,
  ArrowRightIcon,
  MegaphoneIcon,
  HeartIcon,
} from "@phosphor-icons/react";

/**
 * Seller Community Guidelines.
 * The "what's expected of you on Bidorah" page — a sister doc to /policy
 * (which is the wholesale / no-self-promotion rulebook). This one is
 * specifically for sellers and lays out the bar Bidorah holds them to.
 */
export default function SellerGuidelines() {
  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="seller-guidelines-page">
      <Navbar />
      <main className="flex-1 max-w-4xl mx-auto w-full px-6 py-14 sm:py-16">
        {/* ── Hero ───────────────────────────────────────────────────── */}
        <p className="text-xs font-mono tracking-[0.22em] uppercase text-[#1D78AB] font-bold mb-3">
          Seller Community Guidelines
        </p>
        <h1 className="font-display font-black text-5xl lg:text-6xl tracking-tighter leading-[0.95]">
          Sell with care.
          <br />
          <span className="text-[#1D78AB]">Earn the room.</span>
        </h1>
        <p className="mt-6 text-lg text-zinc-700 max-w-2xl leading-relaxed">
          Bidorah only works because the people on stream are the real
          deal — verified humans, real inventory, fair shows, honest
          shipping. These guidelines aren't fine print; they're the
          standard every seller signs up to the moment they go live.
          Read them once, run by them every show.
        </p>

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <Link
            to="/apply-to-sell"
            className="inline-flex items-center gap-1.5 px-5 py-3 rounded-full bg-[#1D78AB] hover:bg-[#165d85] text-white text-sm font-bold"
            data-testid="seller-guidelines-apply-cta"
          >
            Apply to sell <ArrowRightIcon size={14} weight="bold" />
          </Link>
          <Link
            to="/policy"
            className="inline-flex items-center gap-1.5 px-5 py-3 rounded-full border border-zinc-300 text-sm font-semibold text-zinc-800 hover:border-[#1D78AB] hover:text-[#1D78AB]"
            data-testid="seller-guidelines-policy-link"
          >
            Read the rules
          </Link>
        </div>

        {/* ── Five pillars ────────────────────────────────────────────── */}
        <div className="mt-16">
          <p className="text-xs font-mono tracking-widest uppercase text-zinc-500 mb-3">
            The five pillars
          </p>
          <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight">
            What every Bidorah seller commits to.
          </h2>

          <div className="mt-8 grid sm:grid-cols-2 gap-4">
            <Pillar
              icon={<SealCheckIcon size={22} weight="fill" className="text-[#1D78AB]" />}
              n="01"
              title="Be the verified human you signed up as"
              body="One person, one account. The face on your photo ID matches the face running the show. No hand-offs to friends, no shared logins, no alts."
            />
            <Pillar
              icon={<PackageIcon size={22} weight="fill" className="text-[#1D78AB]" />}
              n="02"
              title="Sell what you actually have"
              body="Every lot you put on stream is in your hands, photographed honestly, described truthfully. No bait listings, no 'similar to', no AI-generated photos pretending to be real items."
            />
            <Pillar
              icon={<HandshakeIcon size={22} weight="fill" className="text-[#1D78AB]" />}
              n="03"
              title="Run a fair auction"
              body="No shill bids, no friend-bumping, no alt accounts running prices up. Bidorah's auctions are transparent — everyone bids on the same flat ground."
            />
            <Pillar
              icon={<StorefrontIcon size={22} weight="fill" className="text-[#1D78AB]" />}
              n="04"
              title="Ship like a pro"
              body="Every order goes out within your published handling window. Tracking is uploaded the same day. Packaging is clean, protective, and sized to the item."
            />
            <Pillar
              icon={<ChatCircleDotsIcon size={22} weight="fill" className="text-[#1D78AB]" />}
              n="05"
              title="Treat the room like a guest list"
              body="Buyers, viewers, and other sellers are guests in your show. No harassment, no slurs, no pile-ons. Disagreements get handled with mute / kick — never insults."
            />
            <Pillar
              icon={<HeartIcon size={22} weight="fill" className="text-[#1D78AB]" />}
              n="06"
              title="Hype your peers"
              body="A rising tide lifts every show. Shout out other Bidorah sellers when their shows are great. Buyers who feel a community sticks around twice as long."
            />
          </div>
        </div>

        {/* ── Show quality ────────────────────────────────────────────── */}
        <Section
          overline="Show quality"
          title="Treat your live stream like a show, not a phone call."
        >
          <Bullets
            items={[
              "Camera on you and the lot. Buyers want to see the seller and the item — never just a static slide or a black screen.",
              "Lighting bright enough to read condition. Soft daylight or a single key light beats a phone flashlight every time.",
              "Mic clean, no music over voice. If you take a call, mute. If your kids are around, plan for it.",
              "Vertical (9:16) framing on mobile. Centre-frame your lot in the safe zone — Bidorah's UI overlays the corners.",
              "Show up on time. If your scheduled drop says 7:00 PM ET, you're live by 7:00 PM ET. If you're late, post and reschedule — don't ghost.",
              "Have at least 8–10 lots queued before you go live. Dead air kills momentum.",
            ]}
          />
        </Section>

        {/* ── Listing accuracy ────────────────────────────────────────── */}
        <Section
          overline="Listing accuracy"
          title="Describe it the way you'd want it described to you."
          icon={<CameraIcon size={22} className="text-[#1D78AB]" />}
        >
          <Bullets
            items={[
              "Photos are of the actual item — not a stock photo, not a clone, not an AI render.",
              "Condition is graded honestly. 'NM' means near-mint, not 'looks good in this light.' Damage gets called out before it gets bid on.",
              "Authenticity is yours to vouch for. Reseller of replica? Disclose it loudly. Selling a fake as real is fraud, period.",
              "Measurements, weights, and pull-rates are accurate. Estimates are clearly labeled as estimates.",
              "Edits to a listing's title or photos after a bid is placed are not allowed without seller-led cancellation.",
            ]}
          />
        </Section>

        {/* ── Auction integrity ───────────────────────────────────────── */}
        <Section
          overline="Auction integrity"
          title="No shill bids. No friend bumps. No alt accounts."
          icon={<ScalesIcon size={22} className="text-[#1D78AB]" />}
        >
          <p className="text-sm text-zinc-700 leading-relaxed mb-4">
            Bidorah's auctions are watched by every viewer in real time
            and logged in our database forever. Anything that artificially
            inflates a winning price — your alt, your buddy, a coordinated
            group — is auction manipulation and gets the seat permanently
            revoked. Sellers caught running shill rings are removed from
            the platform and reported to payment processors.
          </p>
          <Bullets
            items={[
              "You cannot bid on your own auction with any account.",
              "Family members, employees, and roommates of the seller cannot bid in that seller's shows.",
              "Coordinated bidding rings (sellers bidding into each other's shows for hype) are an instant ban for all parties involved.",
              "Every reserve must be the real reserve. No 'soft' reserves you'll meet at later.",
            ]}
          />
        </Section>

        {/* ── Shipping ────────────────────────────────────────────────── */}
        <Section
          overline="Shipping & fulfillment"
          title="Ship the item, not the headache."
          icon={<PackageIcon size={22} className="text-[#1D78AB]" />}
        >
          <Bullets
            items={[
              "Default handling window is 3 business days. Custom windows can be set per shop in your dashboard — but whatever you publish, you hit.",
              "Tracking number must be uploaded the same day the package leaves your hands.",
              "Packaging matches the item: bubble mailers for cards, double-boxed for fragile glassware, signature confirmation for $500+ wins.",
              "If you can't ship within window, message the buyer FIRST — before they have to chase you.",
              "Lost-in-transit claims are handled in good faith. Stalling, ignoring, or 'go file with USPS yourself' answers earn warnings.",
              "International orders only if you've enabled them in your shop settings — and only with declared customs forms.",
            ]}
          />
        </Section>

        {/* ── Customer service ────────────────────────────────────────── */}
        <Section
          overline="Customer service"
          title="Speak to buyers like you'd want to be spoken to."
          icon={<ChatCircleDotsIcon size={22} className="text-[#1D78AB]" />}
        >
          <Bullets
            items={[
              "DMs from buyers get an answer within 24 hours, even if the answer is 'I'm checking, give me a day.'",
              "Refund requests for genuine SNAD (significantly not as described) issues are honoured. The buyer ships back at your cost; you refund within 48 hours of receipt.",
              "Disputes don't get personal. Argue the facts, not the buyer.",
              "Block / mute is your tool, not insult. Anyone harassing your show gets removed quietly — Bidorah does the rest.",
            ]}
          />
        </Section>

        {/* ── No self-promotion (zero tolerance) ─────────────────────── */}
        <div className="mt-12 border-2 border-red-600 bg-red-50 rounded-2xl p-6 sm:p-8" data-testid="seller-no-self-promo">
          <div className="flex items-center gap-2 mb-3">
            <MegaphoneIcon size={22} weight="fill" className="text-red-700" />
            <p className="text-xs font-mono tracking-widest uppercase font-bold text-red-700">
              Zero-tolerance — No self-promotion
            </p>
          </div>
          <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight text-red-950">
            Self-promotion on Bidorah is strictly not allowed.
          </h2>
          <p className="mt-4 text-base text-zinc-800 leading-relaxed">
            Bidorah exists to protect sellers and the buyers who came here
            to bid on <em>their</em> shows. Hijacking another seller's
            stream, comments, DMs, or audience to push your own listings,
            handle, off-platform store, or social account is not ethical
            and not professional. Without the explicit consent of the
            seller whose audience you're using, it's a violation of this
            platform and an instant cause for action.
          </p>

          <ul className="mt-5 space-y-2 text-sm text-zinc-900">
            <li>× Promoting your @handle, shop, or off-platform link in another seller's live chat.</li>
            <li>× Posting "follow me" / "check my page" in chat, comments, or DMs to that seller's viewers.</li>
            <li>× Cold-DMing a seller's audience to redirect them to your listings or external stores.</li>
            <li>× Co-opting another seller's brand, drop, or hashtag in your own posts to siphon traffic.</li>
            <li>× Using bots, alt accounts, or scripted spam to amplify your own listings anywhere on Bidorah.</li>
            <li>× Mentioning competing live-shopping platforms in chat to recruit Bidorah viewers.</li>
          </ul>

          <div className="mt-6 rounded-xl bg-red-600 text-white p-4 sm:p-5">
            <p className="font-display font-black text-lg sm:text-xl">
              Self-promoting in another seller's space — without that seller's clear,
              recorded consent — results in an{" "}
              <span className="underline decoration-amber-300 decoration-4 underline-offset-4">
                immediate ban
              </span>
              .
            </p>
            <p className="mt-2 text-sm text-red-50/90 leading-relaxed">
              No warnings. No "first-time" exceptions. The kicked account
              is barred from re-entry and any associated alt accounts are
              removed alongside it. The seller you targeted gets a
              notification that the user was actioned on their behalf.
            </p>
          </div>

          <p className="mt-5 text-xs text-zinc-600 leading-relaxed">
            <b>What's allowed:</b> promoting your own listings on your
            own profile, your own live shows, your own seller posts, the
            channels Bidorah explicitly provides for it (Founder Seller
            referrals, your shop link, your wrap cards), and any
            collaboration where the host has publicly co-signed you as a
            co-host or shouted you out themselves.
          </p>
        </div>

        {/* ── Hard-no list ────────────────────────────────────────────── */}
        <div className="mt-12 border-2 border-red-600 bg-red-50 rounded-2xl p-6 sm:p-8">
          <div className="flex items-center gap-2 mb-3">
            <XCircleIcon size={22} weight="fill" className="text-red-700" />
            <p className="text-xs font-mono tracking-widest uppercase font-bold text-red-700">
              Hard-no list — instant action
            </p>
          </div>
          <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight text-red-950">
            Things that get you removed on day one.
          </h2>
          <ul className="mt-5 space-y-2.5 text-sm text-zinc-900 leading-relaxed">
            <li>
              <b>×</b> Selling counterfeits, replicas, or known-fake reproductions as authentic.
            </li>
            <li>
              <b>×</b> Selling another seller's audience back to your own listings without their explicit consent. (See <Link to="/policy" className="underline text-red-700">Policy → No Self-Promotion</Link>.)
            </li>
            <li>
              <b>×</b> Crossing the wholesale / retail line. Wholesale sellers cannot transact with retail buyers, and vice versa — ever. (See <Link to="/policy" className="underline text-red-700">Policy → Segment Separation</Link>.)
            </li>
            <li>
              <b>×</b> Using more than one account to bid on your own auctions, or running coordinated bid rings with other sellers.
            </li>
            <li>
              <b>×</b> Hate speech, slurs, threats, doxxing, sexual harassment, or anything that crosses platform safety lines.
            </li>
            <li>
              <b>×</b> Selling regulated, restricted, or illegal items (firearms, ivory, controlled substances, stolen goods, anything that triggers a payment-processor flag).
            </li>
            <li>
              <b>×</b> Funneling Bidorah buyers off-platform to dodge fees, taxes, or buyer protection.
            </li>
            <li>
              <b>×</b> Refusing to ship after a buyer has paid. Non-shipping is theft. Repeat offences get reported to authorities.
            </li>
            <li>
              <b>×</b> Sharing buyer PII (addresses, phone numbers, ID details) anywhere outside the order itself.
            </li>
          </ul>
        </div>

        {/* ── Enforcement ladder ─────────────────────────────────────── */}
        <Section
          overline="Enforcement"
          title="How Bidorah handles violations."
          icon={<ShieldCheckIcon size={22} className="text-[#1D78AB]" />}
        >
          <p className="text-sm text-zinc-700 leading-relaxed mb-5">
            Most issues are honest mistakes and we'll tell you privately
            so you can fix them. Patterns or hard-no items skip every
            warning step.
          </p>
          <div className="grid sm:grid-cols-3 gap-3">
            <Step
              n="1"
              tone="amber"
              title="Heads-up"
              body="Soft warning in DM from the Bidorah team. Most sellers never go past this."
            />
            <Step
              n="2"
              tone="orange"
              title="Suspension"
              body="Pause on going live + new listings, usually 7–30 days. Existing orders must still ship."
            />
            <Step
              n="3"
              tone="red"
              title="Permanent ban"
              body="Account closed, payouts held for outstanding orders, return path required, no re-application."
            />
          </div>
          <p className="mt-5 text-xs text-zinc-500 leading-relaxed">
            Hard-no items (counterfeits, fraud, shill rings, hate speech,
            non-shipping) skip to step 3 with no prior warning.
          </p>
        </Section>

        {/* ── How to report ──────────────────────────────────────────── */}
        <Section
          overline="Reporting"
          title="See something off? Tell us."
          icon={<MegaphoneIcon size={22} className="text-[#1D78AB]" />}
        >
          <p className="text-sm text-zinc-700 leading-relaxed mb-4">
            Bidorah has a global report flag on every user, listing, and
            stream. Reports go straight to the admin queue and are
            reviewed within 12 business hours. Reporting is anonymous —
            the seller / user being reported never sees who flagged them.
          </p>
          <Bullets
            items={[
              "Tap the flag icon on a stream, listing, or profile.",
              "Pick the category (counterfeit, harassment, off-platform funneling, shill bidding, etc.).",
              "Add a short note + a screenshot if you have one.",
              "We review, action, and notify the reporter once it's resolved.",
            ]}
          />
        </Section>

        {/* ── The pact ────────────────────────────────────────────────── */}
        <div className="mt-14 rounded-2xl bg-gradient-to-br from-zinc-900 to-zinc-800 text-white p-6 sm:p-10">
          <p className="text-xs font-mono tracking-widest uppercase font-bold text-amber-300 mb-3">
            The Bidorah pact
          </p>
          <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight leading-tight">
            Sell here long enough, you become Bidorah.
          </h2>
          <p className="mt-4 text-base text-zinc-300 leading-relaxed max-w-2xl">
            Bidorah's reputation isn't built by a homepage or a marketing
            line — it's built by every seller, every show, every package
            that gets to a buyer the way they hoped it would. Hold the
            standard, and the standard holds you up. The buyers come back.
            The bids climb. The Founder badge stays gold.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link
              to="/apply-to-sell"
              className="inline-flex items-center gap-1.5 px-5 py-3 rounded-full bg-amber-400 hover:bg-amber-300 text-zinc-950 text-sm font-bold"
            >
              Apply for a Founder seat <ArrowRightIcon size={14} weight="bold" />
            </Link>
            <Link
              to="/why-bidorah"
              className="inline-flex items-center gap-1.5 px-5 py-3 rounded-full border border-white/30 text-sm font-semibold text-white hover:bg-white/10"
            >
              Why Bidorah is different
            </Link>
          </div>
        </div>

        <p className="mt-8 text-xs text-zinc-500 leading-relaxed">
          Bidorah may amend these guidelines at any time as the platform
          evolves. We'll notify all active sellers in the dashboard the
          moment any material change goes live.
        </p>
      </main>
      <Footer />
    </div>
  );
}

// ── Local sub-components ──────────────────────────────────────────────

function Pillar({ icon, n, title, body }) {
  return (
    <div className="rounded-2xl border border-zinc-200 p-5 hover:border-[#1D78AB]/40 transition-colors">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="h-9 w-9 rounded-xl bg-[#1D78AB]/10 grid place-items-center">
          {icon}
        </div>
        <span className="text-xs font-mono text-zinc-400 tracking-wider">{n}</span>
      </div>
      <p className="font-display font-black text-lg leading-tight">{title}</p>
      <p className="mt-2 text-sm text-zinc-600 leading-relaxed">{body}</p>
    </div>
  );
}

function Section({ overline, title, icon, children }) {
  return (
    <section className="mt-12">
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <p className="text-xs font-mono tracking-widest uppercase text-zinc-500 font-bold">
          {overline}
        </p>
      </div>
      <h2 className="font-display font-black text-2xl sm:text-3xl tracking-tight leading-tight">
        {title}
      </h2>
      <div className="mt-5">{children}</div>
    </section>
  );
}

function Bullets({ items }) {
  return (
    <ul className="space-y-2.5 text-sm text-zinc-800 leading-relaxed">
      {items.map((t, i) => (
        <li key={i} className="flex gap-3">
          <CheckCircleIcon size={16} weight="fill" className="text-[#1D78AB] flex-shrink-0 mt-0.5" />
          <span>{t}</span>
        </li>
      ))}
    </ul>
  );
}

function Step({ n, tone, title, body }) {
  const map = {
    amber: { ring: "ring-amber-300", chip: "bg-amber-100 text-amber-900" },
    orange: { ring: "ring-orange-300", chip: "bg-orange-100 text-orange-900" },
    red: { ring: "ring-red-300", chip: "bg-red-100 text-red-900" },
  };
  const c = map[tone] || map.amber;
  return (
    <div className={`rounded-2xl border border-zinc-200 p-4 ring-2 ${c.ring}`}>
      <div className="flex items-center gap-2">
        <span className={`text-[10px] font-mono uppercase tracking-widest font-bold rounded-full px-2 py-0.5 ${c.chip}`}>
          Step {n}
        </span>
        <WarningCircleIcon size={14} className="text-zinc-400" />
      </div>
      <p className="font-display font-bold text-base mt-2">{title}</p>
      <p className="mt-1 text-xs text-zinc-600 leading-relaxed">{body}</p>
    </div>
  );
}
